import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import agarlilogo from "../assets/2025-08-11 (2).png";
import google from "../assets/google-color-svgrepo-com.svg";
import "../components/style.css";
import correct from "../assets/correct-signal-svgrepo-com.svg";
import eye from "../assets/eye-svgrepo-com.svg";
import eyeoff from "../assets/eye-off-svgrepo-com.svg";
import toast from "react-hot-toast";
import { login } from "../services/authService";
import { useAuth } from "../contexts/AuthContext";

export default function SignInPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { loginUser } = useAuth();

  const isValidEmail = (emailValue) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);

  const validate = () => {
    const newErrors = {};
    if (!email.trim()) newErrors.email = "Email is required";
    if (!password.trim()) newErrors.password = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const loginPromise = login(email, password);

      const res = await toast.promise(
        loginPromise,
        {
          loading: "Signing in...",
          success: (data) => {
            if (data?.user?.role === "Admin") {
              return data.message || "Login successful";
            }
            throw new Error("You are not authorized to access this page.");
          },
          error: (err) =>
            err?.response?.data?.message ??
            err?.friendlyMessage ??
            err.message ??
            "Login failed, please check your credentials",
        },
        {
          success: { duration: 1800 },
          error: { duration: 3000 },
        }
      );

      if (res?.user) {
        loginUser(res.user);
      }

      if (res?.user?.role === "Admin") {
        navigate("/dashboard");
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen flex bg-white dark:bg-agarly-dark transition-colors duration-300">
      <div className="px-[50px] w-full flex flex-col justify-center md:px-[120px] py-0 md:w-1/2">
        <img src={agarlilogo} alt="Agarly" className="w-24 h-auto mb-8 md:hidden" />

        <h2 className="text-3xl font-bold mb-2 text-agarly-dark dark:text-agarly-light">Sign In</h2>
        <p className="text-agarly-gray mb-4 text-sm dark:text-gray-400">
          Welcome back. Please enter your details.
        </p>

        <a href="https://myaccount.google.com/?utm_source=sign_in_no_continue&pli=1">
          <button
            className="google-bottons flex items-center justify-center w-full mb-4 transition rounded-agarly-lg"
            type="button"
            style={{
              borderRadius: "10px",
              padding: "2px",
              background: "linear-gradient(135deg, #5DEBFF, #7CC9FF)",
            }}
          >
            <div
              className="google-bottons-content flex items-center justify-center rounded-agarly-lg bg-white dark:bg-agarly-dark"
              style={{
                borderRadius: "10px",
                height: "100%",
                width: "100%",
                padding: "10px",
                transition: "all 0.3s ease",
              }}
            >
              <img src={google} alt="Google" className="w-5 h-5 mr-2" />
              <span className="agarly-gradient-text font-bold text-[14px]">
                Sign in with Google
              </span>
            </div>
          </button>
        </a>

        <div className="flex items-center text-agarly-dark text-sm font-semibold pb-6">
          <div className="flex-1 h-0.5 rounded-full agarly-gradient-bg" />
          <span className="px-3 dark:text-gray-200">Or</span>
          <div className="flex-1 h-0.5 rounded-full agarly-gradient-bg" />
        </div>

        <form className="space-y-5" onSubmit={handleSubmit} noValidate>
          <div className="relative w-full max-w-full">
            <label htmlFor="email" className="block mb-1 text-sm font-medium text-agarly-dark dark:text-gray-200">
              Email
            </label>
            <div className="inputs relative w-full">
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
                }}
                placeholder="your.email@example.com"
                className={`w-full max-w-full rounded-md px-4 py-3 border dark:bg-agarly-dark dark:text-gray-300 ${
                  errors.email ? "border-agarly-danger" : ""
                } focus:outline-none focus:ring-2 ${
                  errors.email ? "focus:ring-agarly-danger/50" : "focus:ring-agarly-primary/50"
                }`}
              />
              {isValidEmail(email) && !errors.email && (
                <span className="correct absolute right-3 top-1/2 -translate-y-1/2 rounded-full">
                  <img src={correct} alt="Valid" className="w-5 h-5" />
                </span>
              )}
            </div>
            {errors.email && <p className="text-agarly-danger text-xs mt-1">{errors.email}</p>}
          </div>

          <div className="relative w-full max-w-full">
            <label htmlFor="password" className="block mb-1 text-sm font-medium text-agarly-dark dark:text-gray-200">
              Password
            </label>
            <div className="inputs relative w-full">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors((prev) => ({ ...prev, password: "" }));
                }}
                placeholder="Password"
                className={`w-full max-w-full rounded-md px-4 py-3 pr-12 border dark:bg-agarly-dark dark:text-gray-300 ${
                  errors.password ? "border-agarly-danger" : ""
                } focus:outline-none focus:ring-2 ${
                  errors.password ? "focus:ring-agarly-danger/50" : "focus:ring-agarly-primary/50"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-agarly-gray hover:text-agarly-dark focus:outline-none"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <img src={showPassword ? eye : eyeoff} className="h-8 w-8" alt="" />
              </button>
            </div>
            {errors.password && <p className="text-agarly-danger text-xs mt-1">{errors.password}</p>}
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center text-sm text-agarly-gray">
              <input type="checkbox" className="mr-2 accent-agarly-primary" />
              Remember me
            </label>
            <Link
              to="#"
              className="agarly-gradient-text hover:underline text-sm font-medium"
            >
              Forgot Password
            </Link>
          </div>

          <button type="submit" className="w-full agarly-btn-primary py-3 text-agarly-dark font-semibold">
            Sign In
          </button>
        </form>
      </div>

      <div className="relative flex-1 hidden md:flex items-center justify-center agarly-gradient-bg p-4 overflow-hidden">
        <img src={agarlilogo} alt="Agarly Dashboard" className="relative z-10 w-[600px]" />
        <div className="absolute w-40 h-40 bg-white/20 top-[-60px] right-[20px] rotate-[20deg]" />
        <div className="absolute w-40 h-40 border-4 border-white/60 p-2 top-[-20px] right-[0px] rotate-[20deg]" />
      </div>
    </div>
  );
}
